/**
 * Ledger-specific date utilities for inventory history and ledger components
 * These functions handle API timestamps, date ranges, and consistent formatting
 */

import { parseDate } from './dateUtils';

/**
 * Get the current user's timezone abbreviation (EDT, PDT, etc.)
 * @returns {string} Timezone abbreviation
 */
const getTimezoneAbbreviation = () => {
  return new Date().toLocaleTimeString('en-US', {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timeZoneName: 'short',
  }).split(' ')[2];
};

/**
 * Converts a UTC timestamp to a formatted local time string with timezone
 * Handles complex timestamp objects from the API with format:
 * {date: "2025-04-11 17:03:53.042991", timezone_type: 1, timezone: "+00:00"}
 * 
 * @param {Object|string|Date} timestamp - UTC timestamp object, date string, or Date
 * @returns {string} Formatted date string with time and timezone
 */
const formatUtcToLocalTimeString = (timestamp) => {
  // Handle UTC timestamp format (object with date and timezone)
  if (timestamp && typeof timestamp === 'object' && timestamp.date) {
    try {
      // Parse the UTC date string
      const utcDateStr = timestamp.date;
      const [datePart, timePart] = utcDateStr.split(' ');
      const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
      const [hours, minutes, secondsWithMs] = timePart.split(':');
      const seconds = parseFloat(secondsWithMs);
      
      // Create date in UTC, then it will be displayed in local timezone
      const utcDate = new Date(Date.UTC(
        year,
        month - 1, // JavaScript months are 0-indexed
        day,
        hours,
        minutes,
        seconds
      ));
      
      return formatLocalTimeString(utcDate);
    } catch (error) {
      console.error('Error parsing UTC timestamp:', error);
      return '';
    }
  }
  
  // If it's a string or Date, parse it and format
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return formatLocalTimeString(date);
};

/**
 * Format a Date object as a string with time and timezone
 * @param {Date} date - JavaScript Date object
 * @returns {string} Formatted date string with time and timezone
 */
const formatLocalTimeString = (date) => {
  if (!date || isNaN(date.getTime())) {
    return '';
  }
  
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneAbbr = getTimezoneAbbreviation();
  
  // Format with time (MM/DD/YYYY h:mm:ss AM/PM EDT)
  const formattedDateTime = date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone
  }).replace(',', '');
  
  return `${formattedDateTime} ${timeZoneAbbr}`;
};

/**
 * Format a Date object with customizable formatting options
 * @param {Date} date - JavaScript Date object
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeTime - Whether to include time component
 * @param {boolean} options.includeTimezone - Whether to include timezone abbreviation
 * @param {boolean} options.use24Hour - Whether to use 24-hour format for time
 * @returns {string} Formatted date string based on provided options
 */
const formatDateToCustomString = (date, options = {}) => {
  const {
    includeTime = true,
    includeTimezone = true,
    use24Hour = false
  } = options;
  
  if (!date || isNaN(date.getTime())) {
    return '';
  }
  
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneAbbr = includeTimezone ? getTimezoneAbbreviation() : '';
  
  if (includeTime) {
    // Format with time (MM/DD/YYYY h:mm AM/PM EDT)
    const formattedDateTime = date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: use24Hour ? '2-digit' : 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: !use24Hour,
      timeZone
    }).replace(',', '');
    
    return includeTimezone ? `${formattedDateTime} ${timeZoneAbbr}` : formattedDateTime;
  }
  
  // Date only (MM/DD/YYYY)
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone
  });
};

/**
 * Format a date for HTML form inputs
 * @param {Date|string} date - Date to format
 * @param {boolean} includeTime - Whether to include time component
 * @returns {string} Formatted date string (YYYY-MM-DD or YYYY-MM-DDThh:mm)
 */
const formatDateForInput = (date, includeTime = false) => {
  const dateObj = date instanceof Date ? date : new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  if (includeTime) {
    // Adjust for local timezone and return YYYY-MM-DDThh:mm
    const localDate = new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 16); // YYYY-MM-DDThh:mm
  }
  
  return dateObj.toISOString().split('T')[0]; // YYYY-MM-DD
};

/**
 * Create a date range for the past N days
 * @param {number} days - Number of days to go back from today
 * @returns {Object} Object with startDate and endDate properties
 */
const createPastDaysRange = (days) => {
  // Create end date as current date with time at 23:59:59
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  
  // Create start date and set to days before with time at 00:00:00
  const start = new Date();
  start.setDate(end.getDate() - days);
  start.setHours(0, 0, 0, 0);
  
  return {
    startDate: start,
    endDate: end
  };
};

/**
 * Create a month-to-date range
 * @returns {Object} Object with startDate (1st of current month) and endDate (now) properties
 */
const createMonthToDateRange = () => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  
  const start = new Date(end.getFullYear(), end.getMonth(), 1);
  start.setHours(0, 0, 0, 0);
  
  return {
    startDate: start,
    endDate: end
  };
};

/**
 * Creates a date range from the beginning of the current year to the current date
 * @returns {Object} Object containing startDate and endDate
 */
const createYearToDateRange = () => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  
  const start = new Date(end.getFullYear(), 0, 1); // January 1st of current year
  start.setHours(0, 0, 0, 0);
  
  return {
    startDate: start,
    endDate: end
  };
};

/**
 * Validate that start date is before or equal to end date
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {boolean} True if valid (start before or equal to end)
 */
const isValidDateRange = (startDate, endDate) => {
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);
  
  return start <= end;
};

/**
 * Convert a date to ISO format string
 * @param {Date|string} date - Date to convert to ISO string 
 * @returns {string} ISO formatted date string
 */
const formatDateToIsoString = (date) => {
  if (!date) {
    return '';
  }
  
  const dateObj = date instanceof Date 
    ? date 
    : new Date(date);
    
  if (isNaN(dateObj.getTime())) {
    console.error('Invalid date provided to formatDateToIsoString:', date);
    return '';
  }
  
  return dateObj.toISOString();
};

export {
  getTimezoneAbbreviation,
  formatUtcToLocalTimeString,
  formatLocalTimeString,
  formatDateToCustomString,
  formatDateForInput,
  createPastDaysRange,
  createMonthToDateRange,
  createYearToDateRange,
  isValidDateRange,
  formatDateToIsoString
}; 